import React from 'react'
import { Tag, Loader2 } from 'lucide-react'
import Drawer from '../ui/Drawer'
import { ContentPost, PostBrand } from '../../services/scoutService'

const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K` : `$${(n || 0).toFixed(0)}`
const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
const humanizeTier = (t: string | null): string => (t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '')

const tierColor = (t: string | null): string => {
  switch ((t || '').toLowerCase()) {
    case 'primary': return 'bg-purple-100 text-purple-700'
    case 'secondary': return 'bg-blue-100 text-blue-700'
    case 'product_placement': return 'bg-amber-100 text-amber-700'
    case 'text_mention': return 'bg-emerald-100 text-emerald-700'
    default: return 'bg-gray-100 text-gray-600'
  }
}

interface Props {
  post: ContentPost | null
  brands: PostBrand[]
  loading: boolean
  isOpen: boolean
  onClose: () => void
}

const BrandsInPostDrawer: React.FC<Props> = ({ post, brands, loading, isOpen, onClose }) => {
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Detected Brands" width="md">
      {!post ? null : (
        <div className="space-y-4">
          {/* Post context */}
          <div className="p-3 rounded-lg border border-gray-100 bg-gray-50/60">
            <p className="text-xs text-gray-600 line-clamp-2">{post.caption || '(no caption)'}</p>
            <div className="mt-1.5 flex items-center gap-2 text-[10px] text-gray-400">
              <span>{fmtDate(post.posted_at)}</span>
              <span>·</span>
              <span>{fmtEMV(post.emv)} EMV</span>
              <span>·</span>
              <span>{post.brand_count} brand{post.brand_count === 1 ? '' : 's'} detected</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
          ) : brands.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No brands detected in this post.</p>
          ) : (
            <div className="space-y-2">
              {brands.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="h-7 w-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Tag className="h-3.5 w-3.5 text-indigo-600" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{b.brand_name}</div>
                      {b.placement_tier && (
                        <span className={`inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${tierColor(b.placement_tier)}`}>
                          {humanizeTier(b.placement_tier)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {/* Detections IN THIS POST (post_detections). NOT sb.total_detections — that
                        brand-level counter is unmaintained and reads 0, which surfaced as
                        "0 detections" for genuinely-detected brands. */}
                    <div className="text-xs"><span className="font-bold text-gray-900">{b.post_detections}</span> <span className="text-gray-500">detection{b.post_detections === 1 ? '' : 's'}</span></div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Last seen {fmtDate(b.last_seen_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  )
}

export default BrandsInPostDrawer
